import React, { useRef, useState } from "react";
import { Bold, Italic, Link as LinkIcon, List, ListOrdered, Quote, Heading, Minus, Eye, EyeOff } from "lucide-react";
import MarkdownContent from "./MarkdownContent";

/**
 * Lightweight Word-like toolbar for textareas that store Markdown.
 * Drop-in replacement for <textarea> — keep the same value/onChange/rows/style.
 *
 * Usage:
 *   <MarkdownEditor value={form.content} onChange={v => set("content", v)} rows={8} placeholder="..." />
 */

const BTN = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  height: "28px", minWidth: "30px", padding: "0 7px",
  background: "transparent", border: "1px solid transparent", borderRadius: "4px",
  cursor: "pointer", color: "#374151", fontFamily: "Manrope", fontSize: "12px", fontWeight: 600,
  transition: "all 0.15s",
};

const SEP = {
  width: "1px", height: "18px", background: "#e5e7eb", margin: "0 4px",
};

export default function MarkdownEditor({ value, onChange, rows = 6, placeholder, style, testId, showPreview = true }) {
  const taRef = useRef(null);
  const [preview, setPreview] = useState(false);

  const surround = (before, after = before) => {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = ta.value;
    const selected = text.slice(start, end) || "текст";
    const next = text.slice(0, start) + before + selected + after + text.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = start + before.length;
      ta.selectionEnd = start + before.length + selected.length;
    });
  };

  const prefixLines = (prefix) => {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = ta.value;
    // Expand selection to whole lines
    const lineStart = text.lastIndexOf("\n", start - 1) + 1;
    const lineEnd = text.indexOf("\n", end);
    const realEnd = lineEnd === -1 ? text.length : lineEnd;
    const block = text.slice(lineStart, realEnd);
    const lines = block.split("\n");
    let isNumbered = prefix === "ORDERED";
    const newBlock = lines.map((l, i) => {
      if (!l.trim()) return l;
      if (isNumbered) return `${i + 1}. ${l.replace(/^\d+\.\s*/, "")}`;
      return `${prefix}${l.replace(/^(-\s|>\s|#+\s)/, "")}`;
    }).join("\n");
    const next = text.slice(0, lineStart) + newBlock + text.slice(realEnd);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = lineStart;
      ta.selectionEnd = lineStart + newBlock.length;
    });
  };

  const insertAtCursor = (snippet) => {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const text = ta.value;
    const next = text.slice(0, start) + snippet + text.slice(start);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + snippet.length;
    });
  };

  const insertLink = () => {
    const ta = taRef.current;
    if (!ta) return;
    const url = window.prompt("Введите URL ссылки:", "https://");
    if (!url) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = ta.value.slice(start, end) || "текст ссылки";
    const snippet = `[${selected}](${url})`;
    const next = ta.value.slice(0, start) + snippet + ta.value.slice(end);
    onChange(next);
  };

  const Btn = ({ onClick, title, children, testId: t }) => (
    <button
      type="button"
      data-testid={t}
      onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.background = "#f3f4f6"; e.currentTarget.style.borderColor = "#e5e7eb"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; }}
      title={title}
      style={BTN}
    >{children}</button>
  );

  return (
    <div style={{ border: "1px solid #d1d5db", borderRadius: "6px", overflow: "hidden", background: "white" }}>
      {/* Toolbar */}
      <div data-testid={testId ? `${testId}-toolbar` : undefined} style={{
        display: "flex", alignItems: "center", flexWrap: "wrap",
        padding: "5px 7px", background: "#f9fafb",
        borderBottom: "1px solid #e5e7eb", gap: "1px",
      }}>
        <Btn onClick={() => surround("**")} title="Жирный (Ctrl+B)" testId="md-bold"><Bold size={14} /></Btn>
        <Btn onClick={() => surround("*")} title="Курсив (Ctrl+I)" testId="md-italic"><Italic size={14} /></Btn>
        <span style={SEP} />
        <Btn onClick={() => prefixLines("# ")} title="Заголовок" testId="md-heading"><Heading size={14} /></Btn>
        <Btn onClick={() => prefixLines("- ")} title="Маркированный список" testId="md-bullet"><List size={14} /></Btn>
        <Btn onClick={() => prefixLines("ORDERED")} title="Нумерованный список" testId="md-ordered"><ListOrdered size={14} /></Btn>
        <Btn onClick={() => prefixLines("> ")} title="Цитата" testId="md-quote"><Quote size={14} /></Btn>
        <span style={SEP} />
        <Btn onClick={insertLink} title="Вставить ссылку" testId="md-link"><LinkIcon size={14} /></Btn>
        <Btn onClick={() => insertAtCursor("\n\n---\n\n")} title="Разделитель" testId="md-hr"><Minus size={14} /></Btn>
        {showPreview && (
          <>
            <span style={{ flex: 1 }} />
            <Btn onClick={() => setPreview(p => !p)} title={preview ? "Скрыть превью" : "Показать превью"} testId="md-preview-toggle">
              {preview ? <EyeOff size={14} /> : <Eye size={14} />}
              <span style={{ marginLeft: "5px", fontSize: "11px" }}>{preview ? "Редактор" : "Превью"}</span>
            </Btn>
          </>
        )}
      </div>

      {/* Editor or preview */}
      {preview ? (
        <div style={{ minHeight: `${rows * 22}px`, padding: "12px 14px", background: "#111", color: "white", maxHeight: "400px", overflow: "auto" }}>
          {value
            ? <MarkdownContent text={value} />
            : <div style={{ color: "#6b7280", fontFamily: "Manrope", fontSize: "13px", fontStyle: "italic" }}>Пусто — нечего показать</div>}
        </div>
      ) : (
        <textarea
          ref={taRef}
          data-testid={testId}
          value={value || ""}
          onChange={e => onChange(e.target.value)}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === "b" || e.key === "B")) { e.preventDefault(); surround("**"); }
            if ((e.ctrlKey || e.metaKey) && (e.key === "i" || e.key === "I")) { e.preventDefault(); surround("*"); }
          }}
          rows={rows}
          placeholder={placeholder}
          style={{
            width: "100%", padding: "10px 12px",
            border: "none", outline: "none",
            fontFamily: "monospace", fontSize: "13px", lineHeight: 1.55,
            resize: "vertical", background: "white", color: "#0A0A0A",
            display: "block", boxSizing: "border-box",
            ...style,
          }}
        />
      )}
    </div>
  );
}
